import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import lottie, { AnimationItem, AnimationEventCallback } from 'lottie-web';

export interface LottieAnimationProps {
  animationAssetsUrl?: string;
  animationDataUrl: string;
  ariaLabel?: string;
  autoplay?: boolean;
  className?: string;
  loop?: boolean;
  onDOMLoaded?: AnimationEventCallback;
  speed?: number;
}

export interface LottieAnimationMethods {
  play(): void;
  pause(): void;
  stop(): void;
}

export const LottieAnimation = forwardRef<
  LottieAnimationMethods,
  LottieAnimationProps
>((props, ref) => {
  const [animationData, setAnimationData] = useState();
  const [isLoaded, setIsLoaded] = useState(false);

  const animationRef = useRef<AnimationItem>();
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    play() {
      if (animationRef.current) {
        animationRef.current.play();
      }
    },
    pause() {
      if (animationRef.current) {
        animationRef.current.pause();
      }
    },
    stop() {
      if (animationRef.current) {
        animationRef.current.stop();
      }
    },
  }));

  const fetchAnimationData = useCallback(
    async (signal) => {
      const response = await fetch(props.animationDataUrl, {
        method: 'GET',
        signal,
      });
      const data = await response.json();
      setAnimationData(data);
    },
    [props.animationDataUrl],
  );

  useEffect(() => {
    // AbortController is used to abort the fetch request if the component is unmounted to avoid a memory leak
    const controller = new AbortController();
    fetchAnimationData(controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchAnimationData]);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    animationRef.current = lottie.loadAnimation({
      animationData,
      assetsPath: props.animationAssetsUrl,
      autoplay: props.autoplay ?? false,
      container: containerRef.current,
      loop: props.loop ?? false,
      renderer: 'svg',
      rendererSettings: {
        viewBoxOnly: true,
      },
    });

    setIsLoaded(animationRef.current.isLoaded);

    return () => {
      if (animationRef.current) {
        animationRef.current.destroy();
      }
    };
  }, [animationData, props.animationAssetsUrl, props.autoplay, props.loop]);

  useEffect(() => {
    if (typeof props.speed === 'number' && isLoaded && animationRef.current) {
      animationRef.current.setSpeed(props.speed);
    }
  }, [props.speed, isLoaded]);

  useEffect(() => {
    if (!props.onDOMLoaded || !isLoaded || !animationRef.current) {
      return undefined;
    }

    animationRef.current.addEventListener('DOMLoaded', props.onDOMLoaded);

    return () => {
      if (animationRef.current) {
        try {
          animationRef.current.removeEventListener(
            'DOMLoaded',
            props.onDOMLoaded,
          );
        } catch (error) {
          console.error(error);
        }
      }
    };
  }, [props.onDOMLoaded, isLoaded]);

  return (
    <div
      aria-label={props.ariaLabel}
      className={props.className}
      ref={containerRef}
    />
  );
});
